import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { AppDispatch } from 'store/store';
import { getFeesAndTaxesState, getSubventionYearOptions } from 'store/selectors/controls.selector';
import { SetFieldValue } from 'modules/core/types/form';
import { FEES_AND_TAXES_FIELD_NAMES, PREFERENCES_PE_FIELD_NAMES } from 'modules/core/shared';
import { OTHER_OPTION } from 'components/Modals/LoanVariablesDialog/modals/Calculators/NetTradeInCalculator/consts/consts';
import { IVehicleModel } from 'modules/core/components/VehicleFilters/types';
import { getVehicleState } from 'store/selectors/vehicle.selector';
import { getRateSheetsDataFlags, getRegionControls } from 'store/selectors/RACAffordabilityEngine.selector';
import { IDealSourceData } from 'modules/core/types/dealSource';
import { ITier } from 'modules/core/types';
import { getCurrentLenderControls } from 'store/selectors/lenders.selector';
import { useArraFinanceBackAdds } from 'modules/core/hooks/useArraFinanceBackAdds';

import { ProbabilityEstimatorCalculationService } from '../../ProbabilityEstimator/services';
import { NET_DOWN_PAYMENT_FIELD_NAMES } from '../../ProbabilityEstimator/probabilityEstimator.const';
import {
  APPROVED_LOAN_TERMS_FIELD_NAMES,
  COMMON_MONTHLY_INCOME_MODAL,
  FRONT_AND_BACK_ENDS_ADDS_FIELD_NAMES,
} from '../const/fieldNames.const';
import { ESTIMATED_FEES_OVERRIDDEN, TRADE_IN_OVERRIDDEN } from '../types/RACAffordabilityEngineForm';
import { ITier as ILenderTier } from '../../Lender/types/Lender.types';

const normalizeForMatch = (value?: string): string => (value || '').trim().toLowerCase();

export const useDealSourceValues = (
  setValue: SetFieldValue,
  isDealSourceDataEmpty: boolean,
  dealSourceData: IDealSourceData,
  resetCount: number,
  isTiersEmpty: boolean,
  max_allowed_pti: number,
  max_allowed_payment: number,
  firstDealSourceRender: boolean,
  setDealSourceTierEditable: ActionCreatorWithPayload<boolean, string>,
  setFirstDealSourceRender: ActionCreatorWithPayload<boolean, string>,
  isTierMatchedWithDealSource: boolean,
  tierOption?: ITier | ILenderTier,
  availableTiers?: ITier[] | ILenderTier[],
  isMakeItWork?: boolean,
  isWithInventory?: boolean,
  isRac?: boolean,
  isAutofillGatedByRouteOneFields?: boolean,
  areRouteOneFieldsEntered?: boolean,
  isLender: boolean = false,
): void => {
  const dispatch: AppDispatch = useDispatch();
  const { insuranceControl } = useSelector(getFeesAndTaxesState);
  const { vehicleModels } = useSelector(getVehicleState);
  const subventionYearOptions = useSelector(getSubventionYearOptions);
  const regionControls = useSelector(getRegionControls);
  const lenderControls = useSelector(getCurrentLenderControls);
  const { isCalculatedPayment } = useSelector(getRateSheetsDataFlags);
  const { isArraFinance, defaultBackAdds } = useArraFinanceBackAdds();

  const dealSourceHighLtvLimit = dealSourceData?.finance_details?.high_ltv_limit;

  const setDealSourceTierValue = useCallback(
    (tierOption: ITier | ILenderTier | undefined, isTierMatchedWithDealSource: boolean) => {
      let appliedTier = tierOption;

      if (!isTierMatchedWithDealSource && availableTiers && availableTiers.length > 1) {
        dispatch(setDealSourceTierEditable(true));
      }

      if (!tierOption && availableTiers && availableTiers.length === 1) {
        appliedTier = availableTiers[0];
      }

      if (appliedTier) {
        setValue(APPROVED_LOAN_TERMS_FIELD_NAMES.TIER, appliedTier, { shouldValidate: true });

        // Max LTV received from the deal source (R1) must not be overwritten by the tier value
        if (!dealSourceHighLtvLimit) {
          setValue(
            PREFERENCES_PE_FIELD_NAMES.OVERRIDE_LTV,
            'max_ltv' in appliedTier ? appliedTier.max_ltv : appliedTier.ltv_limit,
          );
        }
      }
    },
    [availableTiers, dispatch, setDealSourceTierEditable, setValue, dealSourceHighLtvLimit],
  );

  useEffect(() => {
    const shouldAutofillBase =
      isMakeItWork &&
      (isAutofillGatedByRouteOneFields ? true : firstDealSourceRender || !!resetCount) &&
      (isAutofillGatedByRouteOneFields ? areRouteOneFieldsEntered : true) &&
      !isDealSourceDataEmpty &&
      (isWithInventory ? typeof insuranceControl !== 'undefined' : true);

    if (shouldAutofillBase) {
      const {
        primary_monthly_income,
        primary_other_monthly_income,
        coApplicant_monthly_income,
        coApplicant_other_monthly_income,
      } = dealSourceData.income;

      const totalIncome =
        (primary_monthly_income || 0) +
        (primary_other_monthly_income || 0) +
        (coApplicant_monthly_income || 0) +
        (coApplicant_other_monthly_income || 0);

      setValue(COMMON_MONTHLY_INCOME_MODAL, {
        income: {
          totalIncome: totalIncome,
          primaryMonthlyIncome: primary_monthly_income,
          primaryOtherMonthlyIncome: primary_other_monthly_income,
          coApplicantMonthlyIncome: coApplicant_monthly_income,
          coApplicantOtherMonthlyIncome: coApplicant_other_monthly_income,
        },
      });
      setValue(APPROVED_LOAN_TERMS_FIELD_NAMES.GROSS_MONTHLY_INCOME, Math.round(totalIncome));

      let dealSourcePayment = Math.round(dealSourceData.finance_details.payment);

      if (isLender && dealSourceData.finance_details.vsc_payment_allowance) {
        dealSourcePayment = dealSourcePayment - dealSourceData.finance_details.vsc_payment_allowance;
      }

      // Default value: DealSource payment. Exception: if payment = 0, then use SWA calculation elsewhere.
      if (dealSourcePayment > 0) {
        const canCalculatePaymentCap =
          isCalculatedPayment && totalIncome > 0 && max_allowed_payment > 0 && max_allowed_pti > 0;

        if (canCalculatePaymentCap) {
          const calculatedMaxPayment = ProbabilityEstimatorCalculationService.getMaximalMonthlyPayment(
            max_allowed_pti,
            max_allowed_payment,
            totalIncome,
          );

          const paymentToApply =
            calculatedMaxPayment > 0 && calculatedMaxPayment <= dealSourcePayment
              ? calculatedMaxPayment
              : dealSourcePayment;

          setValue(APPROVED_LOAN_TERMS_FIELD_NAMES.DESIRED_MONTHLY_PAYMENT, paymentToApply);
        } else {
          setValue(APPROVED_LOAN_TERMS_FIELD_NAMES.DESIRED_MONTHLY_PAYMENT, dealSourcePayment);
        }
      }
    }

    if (
      shouldAutofillBase &&
      vehicleModels.length > 0 &&
      subventionYearOptions.length > 0 &&
      !isTiersEmpty &&
      (isRac ? Object.keys(regionControls || {}).length > 0 : true)
    ) {
      setDealSourceTierValue(tierOption, isTierMatchedWithDealSource);

      setValue(
        FEES_AND_TAXES_FIELD_NAMES.ESTIMATED_FEES,
        (dealSourceData.taxes_fees.sales_fees || 0) + (dealSourceData.taxes_fees.title_reg_license || 0),
      );
      setValue(
        FEES_AND_TAXES_FIELD_NAMES.SALES_FEES,
        (dealSourceData.taxes_fees.sales_fees || 0) + (dealSourceData.taxes_fees.title_reg_license || 0),
      );

      if (dealSourceData.trade_in_vehicle.value !== 0 || dealSourceData.trade_in_vehicle.amount_owed !== 0) {
        setValue(NET_DOWN_PAYMENT_FIELD_NAMES.TRADE_VALUE, dealSourceData.trade_in_vehicle.value);
        setValue(NET_DOWN_PAYMENT_FIELD_NAMES.AMOUNT_OWED, dealSourceData.trade_in_vehicle.amount_owed);

        if (dealSourceData.trade_in_vehicle.year !== 0) {
          const yearOption = subventionYearOptions.find(
            (year) => year.title === dealSourceData.trade_in_vehicle.year.toString(),
          );

          if (typeof yearOption == 'undefined') {
            setValue(NET_DOWN_PAYMENT_FIELD_NAMES.YEAR, OTHER_OPTION);
            setValue(NET_DOWN_PAYMENT_FIELD_NAMES.OTHER_YEAR, dealSourceData.trade_in_vehicle.year);
          } else setValue(NET_DOWN_PAYMENT_FIELD_NAMES.YEAR, yearOption);
        }

        if (dealSourceData.trade_in_vehicle.make !== '') {
          const dealSourceMake = normalizeForMatch(dealSourceData.trade_in_vehicle.make);
          const dealSourceModel = normalizeForMatch(dealSourceData.trade_in_vehicle.model);
          const makeModelOption = vehicleModels.find(
            (makeModel) => normalizeForMatch(Object.keys(makeModel)[0]) === dealSourceMake,
          );

          if (typeof makeModelOption == 'undefined') {
            setValue(NET_DOWN_PAYMENT_FIELD_NAMES.MAKE, OTHER_OPTION);
            setValue(NET_DOWN_PAYMENT_FIELD_NAMES.MODEL, OTHER_OPTION);
            setValue(NET_DOWN_PAYMENT_FIELD_NAMES.OTHER_MAKE, dealSourceData.trade_in_vehicle.make);
            setValue(NET_DOWN_PAYMENT_FIELD_NAMES.OTHER_MODEL, dealSourceData.trade_in_vehicle.model);
          } else {
            const makeName = Object.keys(makeModelOption)[0];

            setValue(NET_DOWN_PAYMENT_FIELD_NAMES.MAKE, { title: makeName, value: makeName });

            const modelOption = Object.values(makeModelOption)[0].find(
              (model: IVehicleModel) => normalizeForMatch(model.name) === dealSourceModel,
            );

            if (typeof modelOption == 'undefined') {
              setValue(NET_DOWN_PAYMENT_FIELD_NAMES.MODEL, OTHER_OPTION);
              setValue(NET_DOWN_PAYMENT_FIELD_NAMES.OTHER_MODEL, dealSourceData.trade_in_vehicle.model);
            } else setValue(NET_DOWN_PAYMENT_FIELD_NAMES.MODEL, { title: modelOption.name, value: modelOption.name });
          }
        }
      }

      setValue(
        NET_DOWN_PAYMENT_FIELD_NAMES.CASH_DOWN,
        dealSourceData.finance_details.cash_down + dealSourceData.finance_details.rebates,
      );
      setValue(NET_DOWN_PAYMENT_FIELD_NAMES.LIEN_HOLDER, dealSourceData.trade_in_vehicle.lien_holder);
      const netDownPayment = ProbabilityEstimatorCalculationService.getTotalNetDownPayment(
        dealSourceData.finance_details.cash_down + dealSourceData.finance_details.rebates || 0,
        dealSourceData.trade_in_vehicle.value || 0,
        dealSourceData.trade_in_vehicle.amount_owed || 0,
      );

      setValue(NET_DOWN_PAYMENT_FIELD_NAMES.TOTAL_NET_DOWN, netDownPayment);
      setValue(APPROVED_LOAN_TERMS_FIELD_NAMES.NET_DOWN_PAYMENT, netDownPayment);

      let gap = Math.round(dealSourceData.finance_details.gap);
      let vsc = dealSourceData.finance_details.vsc;

      if (isRac) {
        if (regionControls.gapLimit === null) {
          gap = 0;
        } else if (gap > regionControls.gapLimit) {
          gap = regionControls.gapLimit || 0;
        }

        if (regionControls?.vscLimitBelow && vsc > regionControls.vscLimitBelow) {
          vsc = regionControls.vscLimitBelow;
        }
      } else {
        if (lenderControls.maxGap === 0) {
          gap = 0;
        } else if (gap > lenderControls.maxGap) {
          gap = lenderControls.maxGap || 0;
        }

        if (lenderControls.maxVSC === 0) {
          vsc = 0;
        } else if (vsc > lenderControls.maxVSC) {
          vsc = lenderControls.maxVSC || 0;
        }

        if (isArraFinance) {
          vsc = defaultBackAdds;
        }
      }

      setValue(FRONT_AND_BACK_ENDS_ADDS_FIELD_NAMES.GAP, gap);
      setValue(FRONT_AND_BACK_ENDS_ADDS_FIELD_NAMES.VSC, vsc);
      setValue(FRONT_AND_BACK_ENDS_ADDS_FIELD_NAMES.TOTAL_FRONT_AND_BACK_ENDS_ADDS, gap + vsc);
      setValue(APPROVED_LOAN_TERMS_FIELD_NAMES.FRONT_AND_BACK_ENDS_ADDS, gap + vsc);
      setValue('cashDownOverridden', false);
      setValue(ESTIMATED_FEES_OVERRIDDEN, false);
      setValue(TRADE_IN_OVERRIDDEN, false);

      if (dealSourceData.finance_details.high_ltv_limit) {
        setValue(PREFERENCES_PE_FIELD_NAMES.OVERRIDE_LTV, Number(dealSourceData.finance_details.high_ltv_limit));
      }

      dispatch(setFirstDealSourceRender(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isTiersEmpty,
    isDealSourceDataEmpty,
    insuranceControl,
    vehicleModels.length,
    subventionYearOptions.length,
    resetCount,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    Object.keys(regionControls || {}).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    Object.keys(lenderControls || {}).length,
  ]);
};
